/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react'

// Swatch palette for a column created from a checklist task. Orange (first)
// is b4igo's corporate colour and the default selection. Colours match the
// design mockup for this modal.
export const CHECKLIST_COLUMN_SWATCHES = [
  '#FF7C00',
  '#4F91E0',
  '#B57BD6',
  '#5BBF6A',
  '#E0655B',
  '#E8C14A',
  '#4FB3A8',
  '#7B6FE0',
]

interface ChecklistTaskColumnFieldProps {
  theme: string
  columns: any[]
  mode: 'select' | 'new'
  onModeChange: (mode: 'select' | 'new') => void
  selectedColumnId: string
  onSelectColumn: (id: string) => void
  newColumnName: string
  onNewColumnName: (name: string) => void
  newColumnColor: string
  onNewColumnColor: (color: string) => void
  disabled?: boolean
}

// Columns sorted into kanban display order (by position, then insertion).
const sortByPosition = (columns: any[]): any[] =>
  [...(columns || [])].sort(
    (a, b) => (Number(a?.position) || 0) - (Number(b?.position) || 0)
  )

const ChecklistTaskColumnField: React.FC<ChecklistTaskColumnFieldProps> = ({
  theme,
  columns,
  mode,
  onModeChange,
  selectedColumnId,
  onSelectColumn,
  newColumnName,
  onNewColumnName,
  newColumnColor,
  onNewColumnColor,
  disabled,
}) => {
  const isDark = theme === 'dark'
  const [open, setOpen] = useState(false)
  const controlRef = useRef<HTMLDivElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const ordered = sortByPosition(columns)
  const selected = ordered.find((c) => c.id === selectedColumnId)
  // No kanban on this board yet (typical for a note's board). There's
  // nothing to pick, so we force the new-column editor and surface a hint.
  const hasColumns = ordered.length > 0
  const showNewEditor = mode === 'new' || !hasColumns

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (controlRef.current && !controlRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // When the new-column editor shows, focus + select the auto-name so the
  // user can immediately overwrite "Before I go".
  useEffect(() => {
    if (showNewEditor) {
      const t = setTimeout(() => {
        nameInputRef.current?.focus()
        nameInputRef.current?.select()
      }, 0)
      return () => clearTimeout(t)
    }
  }, [showNewEditor])

  return (
    <div className={`ql-checklist-col-field ${isDark ? 'dark' : ''}`}>
      <div className="ql-checklist-col-label">Column</div>

      {!showNewEditor ? (
        <div className="ql-checklist-col-control-wrap" ref={controlRef}>
          <button
            type="button"
            className="ql-checklist-col-control"
            disabled={disabled}
            onClick={() => setOpen((o) => !o)}
          >
            <span className="ql-checklist-col-value">
              {selected ? (
                <>
                  <span
                    className="ql-checklist-col-swatch-sm"
                    style={{ backgroundColor: selected.color || 'transparent' }}
                  />
                  <span className="ql-checklist-col-name">{selected.name?.trim()}</span>
                </>
              ) : (
                <span className="ql-checklist-col-placeholder">Select a column</span>
              )}
            </span>
            <i className={`bi bi-chevron-${open ? 'up' : 'down'} ql-checklist-col-chevron`} />
          </button>

          {open && (
            <div className="ql-checklist-col-menu">
              {ordered.map((col) => (
                <button
                  type="button"
                  key={col.id}
                  className={`ql-checklist-col-option ${
                    col.id === selectedColumnId ? 'selected' : ''
                  }`}
                  onClick={() => {
                    onSelectColumn(col.id)
                    setOpen(false)
                  }}
                >
                  <span
                    className="ql-checklist-col-swatch-sm"
                    style={{ backgroundColor: col.color || 'transparent' }}
                  />
                  <span className="ql-checklist-col-name">{col.name?.trim()}</span>
                  {col.id === selectedColumnId && (
                    <i className="bi bi-check2 ql-checklist-col-check" />
                  )}
                </button>
              ))}
              <button
                type="button"
                className="ql-checklist-col-newlink"
                onClick={() => {
                  setOpen(false)
                  onModeChange('new')
                }}
              >
                <i className="bi bi-plus" /> New column
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="ql-checklist-col-newbox">
          {!hasColumns && (
            <div className="ql-checklist-col-nokanban">
              This board doesn&apos;t have a kanban yet. Name your first column
              below and b4igo will set it up.
            </div>
          )}
          <input
            ref={nameInputRef}
            type="text"
            className="ql-checklist-col-newinput"
            value={newColumnName}
            maxLength={100}
            disabled={disabled}
            onChange={(e) => onNewColumnName(e.target.value)}
            // This input lives inside the parent card's <form>. Without this
            // guard, pressing Enter submits that form — saving the card and
            // closing the whole modal. Swallow Enter so it stays put; the
            // user confirms via the "Create column & save" button.
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.stopPropagation()
              }
            }}
          />
          <div className="ql-checklist-col-swatches">
            {CHECKLIST_COLUMN_SWATCHES.map((color) => (
              <button
                type="button"
                key={color}
                className={`ql-checklist-col-swatch ${
                  newColumnColor?.toUpperCase() === color.toUpperCase() ? 'active' : ''
                }`}
                style={{ backgroundColor: color }}
                disabled={disabled}
                aria-label={`Use colour ${color}`}
                onClick={() => onNewColumnColor(color)}
              />
            ))}
          </div>
          {hasColumns && (
            <button
              type="button"
              className="ql-checklist-col-backlink"
              disabled={disabled}
              onClick={() => onModeChange('select')}
            >
              <i className="bi bi-chevron-left" /> Pick an existing column instead
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default ChecklistTaskColumnField
